<template>
    <div
        v-if="!editable && !isMultiple"
        class="user"
        v-bind:class="{'is-small': small}"
        v-tooltip.top="{content: userFullName, delay: {show: 1000, hide: 0}}"
    >
        <div
            class="user__avatar"
            :class="{'is-text is-initials': useInitials(user)}"
            v-bind:style="userStyle(user)"
            v-if="!isMultiple"
        >{{ useInitials(user) ? userInitials(user) : '' }}</div>
    </div>

    <div v-else-if="multipleInTable" class="users">
        <template v-for="(u, index) in user">
            <div
                class="user is-small"
                v-bind:key="u.id"
                :style="{'zIndex': user.length - index}"
            >
                <div
                    class="user__avatar"
                    :class="{'is-text is-initials': useInitials(u)}"
                    v-bind:style="userStyle(u)"
                >{{ useInitials(u) ? userInitials(u) : '' }}</div>
            </div>
        </template>

    </div>

    <popover
        v-else
        ref="user"
        class="userPopover"
        v-bind:delay="{show:0, hide: 0}"
        v-on:show="onOpen"
    >
        <div
            class="user is-editable"
            :class="{'is-small': small}"
            v-if="!isMultiple"
            v-tooltip.top="{content: 'Doppelklick: nur ich', delay: {show: 800, hide: 0}}"
            @dblclick.stop.prevent="selectCurrentUser"
        >
            <div
                class="user__avatar"
                :class="{'is-text is-initials': useInitials(user)}"
                v-bind:style="userStyle(user)"
            >{{ useInitials(user) ? userInitials(user) : '' }}</div>
            <i class="material-icons is-pointer">edit</i>
        </div>

        <template v-else>
            <div
                class="users"
                v-tooltip.top="{content: 'Doppelklick: nur ich', delay: {show: 800, hide: 0}}"
                @dblclick.stop.prevent="selectCurrentUser"
            >
                <template v-if="user.length > 0">
                    <template v-for="(u, index) in user">
                        <div
                            v-if="index < 8"
                            class="user"
                            v-bind:key="u.id ? u.id : u.username"
                            :class="{'is-small': small, 'is-editable': !index}"
                        >
                            <div
                                class="user__avatar"
                                :class="{'is-text is-initials': useInitials(u)}"
                                v-bind:style="userStyle(u)"
                            >{{ useInitials(u) ? userInitials(u) : '' }}</div>
                            <i v-if="!index" class="material-icons is-pointer">edit</i>
                        </div>
                    </template>
                    <div class="user" v-if="user.length > 8"
                         :class="{'is-small': small}"

                    >
                        <div class="user__avatar is-text">+{{user.length - 8}}</div>
                    </div>
                </template>
                <div class="user is-editable" :class="{'is-small': small}" v-else>
                    <div class="user__avatar" v-bind:style="userStyle(null)"></div>
                    <i class="material-icons is-pointer">edit</i>
                </div>
                <!-- The +n overflow counter keeps plain .is-text (blue), not .is-initials. -->
            </div>
        </template>
        <template slot="popover">
            <multiselect
                v-model="userSelect"
                track-by="id"
                v-bind:custom-label="userLabel"
                v-bind:options="users"

                v-bind:loading="isLoading"

                v-on:search-change = "searchUsers"
                v-bind:internal-search = "false"
                :allow-empty="true"
                :multiple="isMultiple"

                v-on:input="onInput"
            >
                <span slot="caret" />
            </multiselect>

            <div class="buttons level m-t" v-if="isMultiple && quick">
                <button class="button" v-on:click.prevent.stop="selectAllUsers">Alle</button>
                <button class="button is-success" v-on:click.prevent.stop="selectCurrentUser">Nur ich</button>
            </div>
        </template>
    </popover>
</template>



<script>
import isEmpty         from 'lodash/isEmpty'
import Popover         from '@components/Popover';
import { Multiselect } from '@components/form';
import { store }       from '@utilities/store';
import { capitalize, currentUser }  from '@utilities/functions';
import { avatarColorFor }           from '@utilities/avatarColors';


export default {
    store,


    props: {
        user: {
            required: true,
        },

        small: {
            type: Boolean,
            default: false,
            required: false,
        },

        editable: {
            type: Boolean,
            required: false,
            default: false,
        },

        quick: {
            type: Boolean,
            default: false,
        },

        multipleInTable: {
            type: Boolean,
            default: false,
            required: false,
        },

        /** Force the initials avatar even when the object carries no avatarColor yet. */
        initials: {
            type: Boolean,
            default: false,
            required: false,
        },

        /**
         * Selectable users for the editable picker. Omit for staff — the list then
         * comes from the user store as before. Pass a list to drive the picker from
         * somewhere else (the ClientPortal passes its client's portal logins, which
         * the staff store knows nothing about).
         */
        options: {
            type: Array,
            default: null,
            required: false,
        },

        /** Who "nur ich" means. Defaults to the logged-in staff user. */
        current: {
            type: Object,
            default: null,
            required: false,
        },
    },


    computed: {

        isMultiple: function () {
            return Array.isArray(this.user);
        },

        isUser: function () {
            if (!this.isMultiple) {
                return !isEmpty(this.user)
            }
            return false;
        },

        userSelect:  {
            get() {
                if (!this.isMultiple) {
                    if (this.isUser) {
                        const user = this.users
                        .find(user => (this.user.id && user.id === this.user.id) || this.user.username === this.username);
                        return !!user ? user : this.user;
                    }
                } else {
                    if(this.user[0] && this.user[0].id) {
                        return this.users.filter(user => this.user.map(u => u.id).includes(user.id))
                    } else {
                        return this.users.filter(user => this.user.map(u => u.username).includes(user.username))
                    }
                }
            },

            set(user) {
                this.$emit('input', JSON.parse(JSON.stringify(user)));
            }
        },

        userFullName: function () {
            if(this.userIsString(this.user)) {
                return this.user;
            }
            return this.user ? this.userLabel(this.user) : '';
        },

        // A caller-supplied list is already in memory — nothing to wait for.
        hasOwnOptions: function () {
            return Array.isArray(this.options);
        },

        isLoading: function () {
            return this.hasOwnOptions ? false : this.$store.state.users.isLoading;
        },

        users: function () {
            if (this.hasOwnOptions) {
                // Filter locally; the store path searches server-side instead.
                const needle = (this.userSearch || '').trim().toLowerCase();
                return needle
                    ? this.options.filter(user => this.userLabel(user).toLowerCase().includes(needle))
                    : this.options;
            }

            const users = this.$store.state.users.items[this.userSearch];
            return Array.isArray(users) ? users : [];
        },
    },


    data: function () {
        return {
            userSearch: ''
        };
    },


    methods: {
        userIsString: function (user) {
            return typeof user === 'string'
        },

        searchUsers: function (string) {
            this.userSearch = string;
            if (!this.hasOwnOptions) {
                this.$store.dispatch('searchUsers', string);
            }
        },

        userLabel: function (user) {
            if (!user) {
                return '';
            }
            // Portal logins ship a ready fullName (which falls back to the email).
            if (user.fullName) {
                return user.fullName;
            }

            const label = `${capitalize(user.firstName)} ${capitalize(user.lastName)}`;

            if (label.trim().length === 0) {
                return user.username;
            } else {
                return label;
            }
        },

        userAvatarSrc: function (user) {
            let username = null;
            if(user) {
                username = this.userIsString(user) ? user : user.username;
            }
            return username ?  '/clientSpecific/img_user/' + username + '.png' : '/clientSpecific/img_user/default.png';
        },

        /**
         * Initials mode is for ClientPortal logins, which have no avatar PNG. It kicks
         * in when the object carries an avatarColor — a staff User never does, so every
         * existing call site keeps its image avatar untouched. The `initials` prop
         * forces the mode for callers rendering a portal user before the colour loads.
         */
        useInitials: function (user) {
            // "No user selected" keeps the default placeholder image, even when the
            // mode is forced — there are no initials to draw.
            if (!user) {
                return false;
            }
            if (this.userIsString(user)) {
                return this.initials;
            }
            return this.initials || !!user.avatarColor;
        },

        userInitials: function (user) {
            if (!user) {
                return '?';
            }
            if (this.userIsString(user)) {
                return user.substring(0, 2).toUpperCase();
            }
            if (user.initials) {
                return String(user.initials).toUpperCase();
            }

            const first = (user.firstName || '').trim();
            const last = (user.lastName || '').trim();
            if (first || last) {
                return (first.charAt(0) + last.charAt(0)).toUpperCase();
            }

            return String(user.username || user.email || '?').substring(0, 2).toUpperCase();
        },

        userStyle: function (user) {
            if (this.useInitials(user)) {
                // Email before id, matching PortalUser::getAvatarColor() — otherwise a
                // payload without an avatarColor would land on a different palette
                // entry than the server would have computed for the same person.
                const seed = this.userIsString(user) ? user : (user.email || user.id);
                const color = (!this.userIsString(user) && user.avatarColor) || avatarColorFor(seed);

                return { 'background-color': color };
            }
            return {
                'background-image' : `url(${this.userAvatarSrc(user)})`
            }
        },

        onInput: function () {
            if (!this.isMultiple) {
                this.$refs.user.hide()
            }
        },

        onOpen: function () {
            if (!this.hasOwnOptions) {
                this.$store.dispatch('searchUsers', '');
            }
        },

        selectCurrentUser: function () {
            const me = this.current || currentUser();
            if (!me) {
                return;
            }

            const isOnlyMe = this.isMultiple
                && this.user?.length === 1
                && this.user[0]?.id === me?.id;

            if (isOnlyMe) {
                this.userSelect = [];
            } else {
                this.userSelect = [me];
            }
        },

        selectAllUsers: function () {
            if (this.hasOwnOptions) {
                // Clearing, not selecting-everyone: with a caller-supplied list the
                // filter can also match rows that have nobody assigned (portal tasks
                // from a template start that way), and picking every user would drop
                // exactly those. An empty selection means "do not narrow".
                this.userSelect = [];
                return;
            }

            this.userSelect = this.$store.state.users.items[''];
        }
    },


    components: {
        Multiselect,
        Popover
    }
}
</script>
